import {
  CampaignStatus,
  PermissionValues as Permissions,
  useCurrentAccount,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import {
  ColumnData,
  Icon,
  ListCountProvider,
  SlideLayout,
  tokens,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CampaignResponseRow } from './campaignResponsesPanel';
import { CampaignGroupsPanel } from './campaignGroupsPanel';
import { CampaignResponsesPanel } from './campaignResponsesPanel';
import { CampaignsCreationProvider } from './campaignsCreationContext';
import { CampaignThreadEditPanel } from './campaignThreadEditPanel';
import { CampaignThreadPanel } from './campaignThreadPanel';
import { CampaignsList } from './campaignsList';
import { CampaignsFilters } from './campaignsFilters';
import { CampaignsFilterPanel } from './campaignsFilterPanel';
import { CreateCampaignPanel } from './createCampaignPanel';

/**
 * CampaignsPage — top-level SlideLayout page for the Campaigns section.
 *
 * Column states:
 *   SINGLE  — list only (no panel open)
 *   DUAL    — list + CreateCampaignPanel (when '+' is pressed)
 *   DUAL    — list + CampaignResponsesPanel (when a campaign row is clicked)
 *   TRIPLE  — list + CampaignResponsesPanel + CampaignThreadPanel (when a
 *             response row is clicked)
 */
export const CampaignsPage = () => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const canCreate = !!account?.hasPermission(Permissions.campaign.create);
  const [count, setCount] = useState<number | undefined>(undefined);

  // -------------------------------------------------------------------------
  // Selection state
  // -------------------------------------------------------------------------

  /** ID of the campaign currently open in the Responses panel (SENT only). */
  const [selectedCampaignId, setSelectedCampaignId] = useState<
    string | undefined
  >(undefined);

  /** ID of the campaign currently open in the edit panel (DRAFT/SCHEDULED). */
  const [editCampaignId, setEditCampaignId] = useState<string | undefined>(
    undefined
  );

  /** Whether the create-campaign panel is open (takes over the right column). */
  const [showCreatePanel, setShowCreatePanel] = useState(false);

  /** Whether the filter panel is open (takes over the right column). */
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  /** Response row currently open in the Thread panel (third column). */
  const [selectedResponse, setSelectedResponse] = useState<
    CampaignResponseRow | undefined
  >(undefined);

  /** Whether the read-only thread edit panel is open (fourth column). */
  const [showThreadEditPanel, setShowThreadEditPanel] = useState(false);

  /**
   * Groups picker (third column) for the create/edit campaign panel. Non-zero
   * means open; the value increments on each open so the panel remounts and
   * re-seeds from the form's current selection.
   */
  const [groupsPanelOpenId, setGroupsPanelOpenId] = useState(0);

  // -------------------------------------------------------------------------
  // Deep-link entry: when navigated here from the activity feed (aggregated
  // card), location.state carries { selectedCampaignId }. Open the Responses
  // panel for that campaign and clear the state so a back-then-forward nav
  // doesn't re-trigger the open.
  // -------------------------------------------------------------------------
  const location = useLocation();
  const navigate = useNavigate();
  const consumedDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const state = location.state as
      | { selectedCampaignId?: string }
      | null
      | undefined;
    const incoming = state?.selectedCampaignId;
    if (!incoming || consumedDeepLinkRef.current === incoming) return;
    consumedDeepLinkRef.current = incoming;
    setShowCreatePanel(false);
    setShowFilterPanel(false);
    setEditCampaignId(undefined);
    setSelectedResponse(undefined);
    setShowThreadEditPanel(false);
    setGroupsPanelOpenId(0);
    setSelectedCampaignId(incoming);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleRowClick = (row: { id?: string; status?: CampaignStatus }) => {
    if (!row?.id) return;
    setShowCreatePanel(false);
    setSelectedResponse(undefined);
    setShowThreadEditPanel(false);
    setGroupsPanelOpenId(0);
    // DRAFT/SCHEDULED open the edit panel; SENT opens the Responses panel.
    if (
      row.status === CampaignStatus.Draft ||
      row.status === CampaignStatus.Scheduled
    ) {
      setSelectedCampaignId(undefined);
      setEditCampaignId(row.id);
      return;
    }
    setEditCampaignId(undefined);
    setSelectedCampaignId(row.id);
  };

  const handleAddPress = useCallback(() => {
    setSelectedCampaignId(undefined);
    setEditCampaignId(undefined);
    setSelectedResponse(undefined);
    setShowFilterPanel(false);
    setGroupsPanelOpenId(0);
    setShowCreatePanel(true);
  }, []);

  const handleFilterPress = useCallback(() => {
    setSelectedCampaignId(undefined);
    setEditCampaignId(undefined);
    setSelectedResponse(undefined);
    setShowCreatePanel(false);
    setGroupsPanelOpenId(0);
    setShowFilterPanel((open) => !open);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setShowFilterPanel(false);
  }, []);

  const handleCloseCreatePanel = useCallback(() => {
    setShowCreatePanel(false);
    setGroupsPanelOpenId(0);
  }, []);

  const handleCloseEditPanel = useCallback(() => {
    setEditCampaignId(undefined);
    setGroupsPanelOpenId(0);
  }, []);

  const handleCloseResponsesPanel = useCallback(() => {
    setSelectedCampaignId(undefined);
    setSelectedResponse(undefined);
    setShowThreadEditPanel(false);
  }, []);

  const handleCloseThreadPanel = useCallback(() => {
    setSelectedResponse(undefined);
    setShowThreadEditPanel(false);
  }, []);

  const handleResponseSelect = useCallback((row: CampaignResponseRow) => {
    setSelectedResponse(row);
    setShowThreadEditPanel(false);
  }, []);

  const handleOpenThreadEditPanel = useCallback(() => {
    setShowThreadEditPanel(true);
  }, []);

  const handleCloseThreadEditPanel = useCallback(() => {
    setShowThreadEditPanel(false);
  }, []);

  const handleOpenGroupsPanel = useCallback(() => {
    setGroupsPanelOpenId((n) => n + 1);
  }, []);

  const handleCloseGroupsPanel = useCallback(() => {
    setGroupsPanelOpenId(0);
  }, []);

  // -------------------------------------------------------------------------
  // Column data
  // -------------------------------------------------------------------------

  const rightColumn = useMemo<ColumnData | undefined>(() => {
    if (showFilterPanel) {
      return {
        title: t('campaigns.filter.panelTitle', 'Filter campaigns'),
        mainComponent: <CampaignsFilterPanel />,
        showBackButton: true,
        onBackButtonPress: handleCloseFilterPanel,
        disableScroll: true,
      };
    }
    // Create panel takes priority over the responses panel.
    if (showCreatePanel) {
      return {
        title: t('campaigns.create.panelTitle', 'New Campaign'),
        mainComponent: (
          <CreateCampaignPanel
            onClose={handleCloseCreatePanel}
            onCreated={() => handleCloseCreatePanel()}
            onOpenGroupsPanel={handleOpenGroupsPanel}
          />
        ),
        showBackButton: true,
        onBackButtonPress: handleCloseCreatePanel,
        disableScroll: true,
      };
    }

    if (editCampaignId) {
      return {
        title: t('campaigns.edit.panelTitle', 'Edit Campaign'),
        mainComponent: (
          <CreateCampaignPanel
            campaignId={editCampaignId}
            onClose={handleCloseEditPanel}
            onUpdated={() => handleCloseEditPanel()}
            onOpenGroupsPanel={handleOpenGroupsPanel}
          />
        ),
        showBackButton: true,
        onBackButtonPress: handleCloseEditPanel,
        disableScroll: true,
      };
    }

    if (selectedCampaignId) {
      return {
        title: t('campaigns.responses.panelTitle', 'Responses'),
        mainComponent: (
          <CampaignResponsesPanel
            campaignId={selectedCampaignId}
            onResponseSelect={handleResponseSelect}
          />
        ),
        showBackButton: true,
        onBackButtonPress: handleCloseResponsesPanel,
        disableScroll: true,
      };
    }

    return undefined;
  }, [
    showFilterPanel,
    showCreatePanel,
    editCampaignId,
    selectedCampaignId,
    t,
    handleCloseFilterPanel,
    handleCloseCreatePanel,
    handleCloseEditPanel,
    handleCloseResponsesPanel,
    handleResponseSelect,
    handleOpenGroupsPanel,
  ]);

  const thirdColumn = useMemo<ColumnData | undefined>(() => {
    // Groups picker for the create/edit campaign panel takes the third column
    // (create/edit and the responses→thread flow are mutually exclusive, so the
    // column is free here).
    if (
      groupsPanelOpenId > 0 &&
      (showCreatePanel || !!editCampaignId) &&
      workspace?.id
    ) {
      return {
        title: t('campaigns.create.groups', 'Groups'),
        mainComponent: (
          <CampaignGroupsPanel
            key={groupsPanelOpenId}
            workspaceId={workspace.id}
          />
        ),
        showBackButton: true,
        onBackButtonPress: handleCloseGroupsPanel,
        disableScroll: true,
      };
    }

    if (!selectedResponse?.issueId) return undefined;
    return {
      rawTitle: selectedResponse.threadTitle,
      title: selectedResponse.threadTitle,
      mainComponent: (
        <CampaignThreadPanel
          responseRow={selectedResponse}
          issueId={selectedResponse.issueId}
        />
      ),
      headerComponent: (
        <Pressable
          onPress={handleOpenThreadEditPanel}
          accessibilityRole="button"
          accessibilityLabel={t(
            'campaigns.thread.editLabel',
            'View thread details',
          )}
          testID="campaign-thread-edit-button"
          style={styles.headerButton}
        >
          <Icon name="Settings" size={20} />
        </Pressable>
      ),
      showBackButton: true,
      onBackButtonPress: handleCloseThreadPanel,
      disableScroll: true,
    };
  }, [
    selectedResponse,
    handleCloseThreadPanel,
    handleOpenThreadEditPanel,
    t,
    groupsPanelOpenId,
    showCreatePanel,
    editCampaignId,
    workspace,
    handleCloseGroupsPanel,
  ]);

  const fourthColumn = useMemo<ColumnData | undefined>(() => {
    if (!showThreadEditPanel || !selectedResponse?.issueId) return undefined;
    return {
      title: t('issue.editModal.header', 'Edit'),
      mainComponent: (
        <CampaignThreadEditPanel issueId={selectedResponse.issueId} />
      ),
      showBackButton: true,
      onBackButtonPress: handleCloseThreadEditPanel,
      disableScroll: true,
    };
  }, [
    showThreadEditPanel,
    selectedResponse,
    handleCloseThreadEditPanel,
    t,
  ]);

  const viewMode = rightColumn ? ViewMode.DUAL : ViewMode.SINGLE;

  return (
    <CampaignsCreationProvider value={{ openNew: handleAddPress }}>
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={{
          title: t('campaigns.page.title', 'Campaigns'),
          count,
          headerComponent: (
            <CampaignsFilters
              onAddPress={canCreate ? handleAddPress : undefined}
              showAddButton={canCreate}
              onFilterPress={handleFilterPress}
            />
          ),
          mainComponent: (
            <CampaignsList
              onRowClick={handleRowClick}
              onItemsCountChange={setCount}
            />
          ),
          disableScroll: true,
        }}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        fourthColumn={fourthColumn}
        viewMode={viewMode}
      />
    </ListCountProvider>
    </CampaignsCreationProvider>
  );
};

const styles = StyleSheet.create({
  headerButton: {
    padding: tokens.spacing.sm,
  },
});
